import * as Yup from "yup";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import { ReactComponent as Naira } from "../../../../svg/naira.svg";
import { NotificationContext } from "../../../../providers/notification";
import apiCall from "../../../../utils/apiCall";

interface ApprovalTypeData {
  ApprovalTypeId: number;
  ApprovalTypName: string;
}
export interface RouteTypeData {
  RouteId: number;
  RouteName: string;
}

interface membershipData {
  Key: number;
  Value: string;
}

interface PayloadData {
  RoutPaymentId: string;
  RoutId: number;
  RouteName: string;
  ActionId: number;
  ActionName: string;
  Amount: number;
  PaymentActive?: boolean;
  OrderOfPayment?: number;
  TargetedMembersId: number;
  TargetedMembers?: number;
}

interface ComponentProps {
  handleClose: () => void;
  RoutePaymentDetails: any;
}

const UpdateRoutePayment: React.FC<ComponentProps> = ({
  handleClose,
  RoutePaymentDetails,
}) => {
  const [Name, setName] = useState("");
  const [action, setAction] = useState("");
  const queryClient = useQueryClient();
  const [targetMembers, setTargetMembers] = useState("");

  const { showNotification } = useContext(NotificationContext);

  const GetApprovalTypes = async (): Promise<any> => {
    return await apiCall().get(`ApprovalType/GetApprovalTypes`);
  };

  const GetMembershipTypes = async (): Promise<any> => {
    return await apiCall().get(`Routes/GetMembershipTypes`);
  };

  const GetAllRoute = async (): Promise<any> => {
    return await apiCall().get(`Routes/GetAllRoute`);
  };

  const [getApprovalTypeQuery, getMembershipTypeQuery, getAllRouteQuery] =
    useQueries({
      queries: [
        { queryKey: ["get-appproval"], queryFn: GetApprovalTypes },
        { queryKey: ["get-membership"], queryFn: GetMembershipTypes },
        { queryKey: ["get-routes"], queryFn: GetAllRoute },
      ],
    });

  const approvalTypeOption =
    getApprovalTypeQuery && getApprovalTypeQuery?.data?.data?.length > 0 ? (
      getApprovalTypeQuery?.data?.data.map((approval: ApprovalTypeData) => (
        <option value={approval?.ApprovalTypeId} key={approval?.ApprovalTypeId}>
          {approval?.ApprovalTypName}
        </option>
      ))
    ) : (
      <option disabled>
        {getApprovalTypeQuery?.isLoading
          ? "loading...."
          : getApprovalTypeQuery?.error
          ? "error"
          : ""}
      </option>
    );

  const RouteTypeOption =
    getAllRouteQuery && getAllRouteQuery?.data?.data?.Data?.length > 0 ? (
      getAllRouteQuery?.data?.data?.Data.map((route: RouteTypeData) => (
        <option value={route?.RouteId} key={route?.RouteId}>
          {route?.RouteName}
        </option>
      ))
    ) : (
      <option disabled>
        {getAllRouteQuery?.isLoading
          ? "loading...."
          : getAllRouteQuery?.error
          ? "error"
          : ""}
      </option>
    );

  const MembershipTypeOption =
    getMembershipTypeQuery && getMembershipTypeQuery?.data?.data?.length > 0 ? (
      getMembershipTypeQuery?.data?.data?.map((member: membershipData) => (
        <option value={member?.Key} key={member?.Key}>
          {member?.Value}
        </option>
      ))
    ) : (
      <option disabled>
        {getMembershipTypeQuery?.isLoading
          ? "loading...."
          : getMembershipTypeQuery?.error
          ? "error"
          : ""}
      </option>
    );

  const postRoutePayment = (data: any) => {
    return apiCall().post("/Routes/UpdatePaymentRoute", data);
  };

  const postRoutePaymentMutation = useMutation(
    ["post-Route"],
    postRoutePayment,
    { retry: 0 }
  );

  const postRoutePaymentHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    const routePaymentPayload = {
      RoutPaymentId: RoutePaymentDetails.RoutPaymentId,
      RoutId: values?.route,
      RoutName: Name,
      PaymentActive: true,
      ActionId: values?.approvalType,
      ActionName: action,
      Amount: values?.amount,
      Amount_B: values.nyscAmount,
      OrderOfPayment: values?.paymentOrder,
      TargetedMembersId: values?.targetMembership,
      TargetedMembers: targetMembers,
    };

    try {
      await postRoutePaymentMutation.mutateAsync(routePaymentPayload, {
        onSuccess: () => {
          showNotification({
            message: "Route Payment successfully updated",
            type: "success",
          });
          queryClient.refetchQueries(["get-routePayment"]);
          handleClose();
          resetForm();
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.title || error?.message,
        type: "error",
      });
    }
  };

        const validationSchema = Yup.object().shape({
        route: Yup.number().required(),
        targetMembership: Yup.number().required(),
        approvalType: Yup.number().required(),
        paymentOrder: Yup.number()
          .typeError("Only numbers are allowed")
          .required("Payment order is required"),
        amount: Yup.number()
          .typeError("Only numbers are allowed")
          .required("Amount is required"),
        nyscAmount: Yup.number()
          .typeError("Only numbers are allowed")
          .nullable(), // optional
      });

  const formik = useFormik<FormikValues>({
    initialValues: {
      route: RoutePaymentDetails.RoutId ?? 0,
      targetMembership: RoutePaymentDetails.TargetedMembersId ?? 0,
      approvalType: RoutePaymentDetails.ActionId ?? 0,
      paymentOrder: RoutePaymentDetails.OrderOfPayment ?? 0,
      amount: RoutePaymentDetails.Amount ?? 0,
      nyscAmount: RoutePaymentDetails.Amount_B ?? 0,
        },
        onSubmit: (values, { resetForm }) => {
      postRoutePaymentHandler(values, resetForm);
    },
    validationSchema,
  });

  useEffect(() => {
    const filteredRoutes = getAllRouteQuery?.data?.data?.Data?.filter(
      (item: RouteTypeData) => item?.RouteId == formik?.values?.route
    );
    getAllRouteQuery?.data?.data?.Data &&
      setName(filteredRoutes[0]?.RouteName || "");
  }, [formik?.values?.route]);

  useEffect(() => {
    const filteredRoutes = getApprovalTypeQuery?.data?.data?.filter(
      (item: any) => item.ApprovalTypeId == formik?.values?.approvalType
    );
    getApprovalTypeQuery?.data?.data &&
      setAction(filteredRoutes[0]?.ApprovalTypName || "");
  }, [formik?.values?.approvalType]);

  useEffect(() => {
    const filteredRoutes = getMembershipTypeQuery?.data?.data?.filter(
      (item: any) => item.Key == formik?.values?.targetMembership
    );
    getMembershipTypeQuery?.data?.data &&
      setTargetMembers(filteredRoutes[0]?.Value || "");
  }, [formik?.values?.targetMembership]);  

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <StyledInputWrapper>
          <Field
            name="route"
            as={Select}
            label="Membership Route"
            options={RouteTypeOption}
            disabled={true}
          />

          <Field
            name="approvalType"
            as={Select}
            label="Approval Type"
            options={approvalTypeOption}
            disabled={true}
          />

          <Field
            name="targetMembership"
            type="text"
            as={Select}
            label="Target Membership"
            options={MembershipTypeOption}
            disabled={true}
          />

          <Field
            name="paymentOrder"
            type="number"
            as={Input}
            label="Payment Order"
            placeholder="Payment Order"
            disabled={true}
            // options={paymentOrderOptions}
          />

          <AmountInputWrapper>
            <StyledAmount>
              <Naira />
            </StyledAmount>
            <Field
              name="amount"
              type="text"
              as={StyledInput}
              label="Amount"
              placeholder="Amount"
            />
          </AmountInputWrapper>

          {RoutePaymentDetails.RoutId === 1 && (
            <AmountInputWrapper>
              <StyledAmount>
                <Naira />
              </StyledAmount>

              <Field
                name="nyscAmount"
                type="text"
                as={StyledInput}
                label="NYSC Amount"
                placeholder="NYSC Amount"
              />
            </AmountInputWrapper>
          )}
        </StyledInputWrapper>
      </form>

      <ButtonWrapper>
        <ButtonContainer>
          <StyledButton text="Cancel" type="button" onClick={handleClose} />
          <StyledButton
            onClick={formik?.handleSubmit}
            type="submit"
            text={postRoutePaymentMutation.isLoading ? "Submitting" : "Submit"}
            disabled={postRoutePaymentMutation.isLoading}
          />
        </ButtonContainer>
      </ButtonWrapper>
    </FormikProvider>
  );
};

export default UpdateRoutePayment;

const FormWrapper = styled("div")`
  margin-inline: 3.5rem;
`;
const StyledInputWrapper = styled("div")`
  display: grid;
  flex-flow: row wrap;
  align-items: start;
  column-gap: 4rem;
  margin-inline: 4rem;

  @media (min-width: 1038px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 587px) {
    justify-content: center;
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;

const StyledAmount = styled("span")`
  background-color: #11643c;
  border: 0.1rem solid #11643c;
  padding: 1rem 1.2rem;
  position: absolute;
  top: 48.1px;
  z-index: 9;
`;

const AmountInputWrapper = styled("div")`
  position: relative;
`;

const StyledInput = styled(Input)`
  padding: 1.2rem 4.4rem;
`;
